import React, { useState } from 'react';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';

/**
 * Ligações do CDR que não entraram em cliente nenhum (etiqueta não reconhecida).
 *
 * Até 05/10/2026 o aviso era um link discreto, e a lista abria por
 * `document.getElementById(...).classList.toggle`, que some no primeiro re-render. Em
 * 04/10 foram 621 de 59.992. E o PABX trocou o formato: a etiqueta deixou de identificar
 * o cliente, então um dia o descarte chega a 100% e continuaria discreto. Processar cego
 * não pode ser calado: acima de 1% a lista abre sozinha e o aviso vira faixa.
 */

export interface DescarteResumo {
  totalRecords?: number;
  processedRecords?: number;
  unrecognizedTags?: Array<{ tag: string; count: number }>;
}

/** Fração acima da qual a lista abre sozinha. */
export const LIMITE_DO_DESCARTE = 0.01;

export function medirDescarte(s: DescarteResumo | null | undefined) {
  const total = Number(s?.totalRecords) || 0;
  const processados = Number(s?.processedRecords) || 0;
  const descartados = Math.max(0, total - processados);
  const fracao = total > 0 ? descartados / total : 0;
  return { total, descartados, fracao, abreSozinho: fracao > LIMITE_DO_DESCARTE, tudo: total > 0 && processados === 0 };
}

const pct = (f: number) => `${(f * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

export default function DescarteDoCdr({ resumo }: { resumo: DescarteResumo | null | undefined }) {
  const m = medirDescarte(resumo);
  // null = segue a regra (abre acima de 1%); depois do primeiro clique, vale a escolha de quem lê.
  const [aberto, setAberto] = useState<boolean | null>(null);
  if (m.descartados === 0) return null;
  const mostrar = aberto ?? m.abreSozinho;
  const tags = resumo?.unrecognizedTags || [];
  const tom = m.tudo ? 'danger' : 'warning';
  const frase = m.tudo
    ? `Nenhuma das ${m.total.toLocaleString('pt-BR')} ligações foi reconhecida: a etiqueta não corresponde a cliente nenhum. Confira o formato do arquivo antes de aplicar qualquer coisa.`
    : `${m.descartados.toLocaleString('pt-BR')} de ${m.total.toLocaleString('pt-BR')} ligações (${pct(m.fracao)}) foram descartadas: a etiqueta não corresponde a nenhum cliente. Elas não entram em fatura nenhuma.`;

  return (
    <div
      className={`mt-4 rounded-lg ${m.abreSozinho ? `border p-3 ${TONE_BORDER[tom]} ${TONE_SURFACE[tom]}` : ''}`}
      role={m.abreSozinho ? 'alert' : undefined}
      data-testid='cdr-descarte'
    >
      <button
        type='button'
        onClick={() => setAberto(!mostrar)}
        aria-expanded={mostrar}
        className={`text-xs ${TONE_TEXT[tom]} flex items-start gap-1 text-left`}
      >
        <span aria-hidden='true'>⚠</span>
        <span>{frase}</span>
        {tags.length > 0 && <span aria-hidden='true' className='shrink-0'>{mostrar ? '▾' : '▸'}</span>}
      </button>
      {mostrar && tags.length > 0 && (
        <div className='mt-2 bg-slate-800 rounded-lg p-3 max-h-40 overflow-y-auto' data-testid='cdr-descarte-tags'>
          <table className='w-full text-xs'>
            <thead>
              <tr className='text-slate-400'>
                <th className='text-left pb-2'>Etiqueta</th>
                <th className='text-right pb-2'>Ligações</th>
              </tr>
            </thead>
            <tbody>
              {tags.slice(0, 20).map((item, idx) => (
                <tr key={idx} className='text-slate-300 border-t border-slate-700'>
                  <td className='py-1 font-mono'>{item.tag}</td>
                  <td className={`py-1 text-right ${TONE_TEXT.warning}`}>{item.count}</td>
                </tr>
              ))}
              {tags.length > 20 && (
                <tr className='text-slate-500'>
                  <td colSpan={2} className='py-1 text-center'>... e mais {tags.length - 20} etiquetas</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
